/**
 * Authentication Service — patrol-web thin build over the canonical factory.
 *
 * AUTHSVC-CONSOLIDATION-001 (Phase 3): the axios husk that owned every tenant
 * auth HTTP op (login, cross-app SSO exchange, passkey HTTP legs, token
 * refresh) is retired in favour of `@puffin-core/auth-client` `createAuthService`.
 * The factory owns ALL endpoint wiring, response extraction, error handling, and
 * token persistence; this file only:
 *   1. builds the transport over patrol-web's dedicated `/api/v1/auth` client
 *      pair (`createApiClientTransport`),
 *   2. selects the patrol shape (`mfaSetupShape: 'throw'` and the tenant
 *      `refreshEndpoint` for `refreshTenantToken`),
 *   3. re-exports the SAME named surface PatrolPuffin's `createAuthContext`
 *      wiring + consumers (login-shell's usePasskeyLogin) depend on, by SPREADING the factory's
 *      pre-aliased context projections (AUTHSVC-CONSOLIDATION-002).
 *
 * A TENANT service only (Q-L16-98, 2026-09-29): the DUAL mode and its platform
 * legs are retired — platform staff sign in through the console's own
 * `portals/platform-admin` context and service.
 *
 * Surface mapping (factory surface → patrol export):
 *   - factory.contextSurface (SPREAD) → login/logout/validateToken/verifyMfa/
 *     sendMfaOtp/authenticateWithPasskey/exchangeCrossAppToken (tenant legs)
 *   - refreshToken                      → refreshTenantToken ({token})
 *   - getCurrentUser / getToken         → same names
 *   - clearAuthData                     → same names
 *   - getPasskeyRegisterOptions / registerPasskey / getPasskeyDiscoverableOptions
 *     / getPasskeys / deletePasskey → same names
 *
 * patrol divergences vs the other tenant apps:
 *   - patrol-web's tenant login never returns an MFA challenge, but its
 *     `createAuthContext` config carries `mfa: true`. Spreading `contextSurface`
 *     GAINS an inert `verifyMfa`/`sendMfaOtp` — desired: it closes the latent
 *     `mfa:true`-without-`verifyMfa` gap.
 *   - patrol-web does NOT consume `getLocations`; `contextSurface` omits all
 *     plain getters, so this omission is preserved (NOT re-exported).
 *
 * The discoverable passkey-login ORCHESTRATOR (browser ceremony + HTTP legs) is
 * NOT here — it is `@puffin-core/login-shell` `authenticateWithDiscoverablePasskey`
 * (run by `usePasskeyLogin` on the login page), composing this service's
 * `getPasskeyDiscoverableOptions` + `authenticateWithPasskey` HTTP legs with the
 * canonical `@puffin-core/mfa-ui` `WebAuthnService` ceremony (USER-FACING-GEMS L1).
 *
 * SETAUTHHEADER-RETIRE (2026-10-26; console's Ruling 35, finished fleet-wide): the `setAuthHeader` sync of the stored
 * bearer onto `axios.defaults` is gone. Every authed request already reads the token per request through
 * `@puffin-core/api-client`'s `getToken`, and the global copy only leaked the bearer into clients built after a sign-in.
 *
 * @module features/auth/services/authService
 */

import { createApiClientTransport, createAuthService } from '@puffin-core/auth-client';
import { translateAuthError } from '@puffin-core/login-shell';
import { authStorage, REFRESH_ENDPOINT } from '../../../core/api/auth/authStorage';
import { publicAuthClient, authedAuthClient } from '../../../core/api/auth/authClient';

/**
 * Canonical patrol-web auth-service SUPERSET — the factory object selected to the
 * patrol shape. `mfaSetupShape: 'throw'` keeps any MFA-setup ops positional +
 * throw; `refreshEndpoint` backs `refreshTenantToken`.
 */
const factory = createAuthService({
  http: createApiClientTransport({
    publicClient: publicAuthClient,
    authedClient: authedAuthClient,
  }),
  tokenStore: authStorage,
  errorTranslator: translateAuthError,
  mfaSetupShape: 'throw',
  refreshEndpoint: REFRESH_ENDPOINT,
});

/**
 * PatrolPuffin auth service — the canonical factory surface aliased to the exact
 * method names + signatures every consumer (and `createAuthContext`) already
 * imports. No raw HTTP here; the factory owns all of it.
 *
 * AUTHSVC-CONSOLIDATION-002: the catastrophic-silent context projections
 * (`login`/`logout`/`validateToken`/`verifyMfa`/`sendMfaOtp`/
 * `authenticateWithPasskey`/`exchangeCrossAppToken`) are no longer hand-aliased —
 * they are SPREAD from the factory's pre-aliased `contextSurface`. This
 * structurally eliminates the
 * MFA-LOGIN-CONSOLIDATION-001 typo class (`verifyMfa: factory.verifyMfa`).
 *
 * NOTE (patrol divergences): spreading `contextSurface` GAINS an inert
 * `verifyMfa`/`sendMfaOtp` for patrol-web (its tenant login never returns an MFA
 * challenge, but its `createAuthContext` config carries `mfa: true`) — this
 * closes a latent `mfa:true`-without-`verifyMfa` gap and is correct/desired. The
 * `contextSurface` deliberately omits all plain getters, so patrol's
 * long-standing `getLocations` omission is preserved (still NOT re-exported).
 */
export const authService = {
  ...factory.contextSurface,
  // Tenant access-token refresh ({token})
  refreshTenantToken: factory.refreshToken,
  // Local getters
  getCurrentUser: factory.getCurrentUser,
  getToken: factory.getToken,
  // Clear local auth state
  clearAuthData: factory.clearAuthData,
  // Passkey (WebAuthn) HTTP legs
  getPasskeyRegisterOptions: factory.getPasskeyRegisterOptions,
  registerPasskey: factory.registerPasskey,
  getPasskeyDiscoverableOptions: factory.getPasskeyDiscoverableOptions,
  getPasskeys: factory.getPasskeys,
  deletePasskey: factory.deletePasskey,
  // MFA setup (throw shape: positional args, throw on error, return payload) — the shared setup router's five
  // in-session operations patrol-api mounts at /v1/auth/mfa, driven by `@puffin-core/mfa-ui`'s `<MfaSettingsCard>` on
  // the Security page (TENANT-ID-ORPHAN-REPAIR-001 S6b card unit 4; planner's surface).
  initMfaSetup: factory.initMfaSetup,
  verifyMfaSetup: factory.verifyMfaSetup,
  regenerateBackupCodes: factory.regenerateBackupCodes,
  getMfaStatus: factory.getMfaStatus,
  disableMfa: factory.disableMfa,
};

/**
 * MKT-LOGIN-XDOMAIN-001 — RAW (no-AuthProvider) auth legs for the cross-domain
 * marketing login (`www.patrolpuffin.com/login`).
 *
 * The public marketing site has no AuthProvider, so `createMarketingLoginAuth`
 * owns its own auth state and needs the factory's RAW projections: the raw
 * tenant `login({email,password,subdomain,captchaToken})` (returns the raw
 * `{requiresMfa|accessToken|refreshToken|user}` shape WITHOUT persisting — the
 * marketing hook persists via the injected `storeAuthData`) and the
 * never-throws result-object `verifyMfa`/`sendMfaOtp`.
 *
 * These deliberately bypass the context-shaped `authService` export above: in
 * patrol's DUAL posture its `login` is the PLATFORM leg and its `verifyMfa` is
 * the THROWING context projection — both wrong for the tenant marketing surface.
 */
export const marketingLoginAuthService = {
  login: factory.login,
  verifyMfa: factory.verifyMfa,
  sendMfaOtp: factory.sendMfaOtp,
};

export default authService;
